import React, { useState } from "react";
import { MessageSquarePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { errMessage } from "@/lib/api";

const CATEGORIES = ["Bug", "Suggestion", "Question", "Other"];

// Authenticated dashboard feedback — posts to the existing Contact/Support inbox
// via /feedback, which auto-associates the logged-in user's name & email.
export function FeedbackButton({ variant = "outline", className = "" }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("Suggestion");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    setSending(true);
    try {
      await api.post("/feedback", { category, message });
      toast.success("Thanks for your feedback! We've received it.");
      setOpen(false);
      setMessage("");
      setCategory("Suggestion");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} className={className} data-testid="feedback-open-btn">
          <MessageSquarePlus className="mr-2 h-4 w-4" /> Send Feedback
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle>Send Feedback</DialogTitle>
          <DialogDescription>Tell us what's working or what could be better — sent from your account.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="feedback-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`feedback-cat-${c.toLowerCase()}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Message <span className="text-slate-400">(optional)</span></Label>
            <Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Add any details…" data-testid="feedback-message" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={sending} className="bg-[#0A2540] text-white hover:bg-[#0c2e4e]" data-testid="feedback-submit">
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
